<template>
  <div id="app" class="default-layout">
    <div ref="appHeader" class="default-layout__top-bar">
      <Header
        v-if="
          isInitialized &&
            isLoggedInAndAuthenticated &&
            !shouldHideHeaderAndSideBar
        "
        @click-burger="clickBurger"
        @show-search-form="changeSearchFormIsDisplayed"
        @show-filter-form="changeFilterFormIsDisplayed"
      />
    </div>

    <template v-if="!isInitialized">
      <Loading class="default-layout__loading" />
    </template>

    <template v-else>
      <V3SideBarNew
        v-if="isLoggedInAndAuthenticated && !shouldHideHeaderAndSideBar"
        class="default-layout__side-bar"
      />

      <div
        class="app_savedSearchBalloon"
        :style="style"
        @mouseenter="onMouseEnterSavedSearchBalloon"
        @mouseleave="onMouseLeaveSavedSearchBalloon"
      >
        <SavedSearchBalloon
          v-if="showBalloon"
          :favorite-search="hoveredFavoriteSearch"
          :adjust="balloonArrowAdjust"
        />
      </div>

      <div
        ref="appContent"
        class="default-layout__main"
        data-cy="ug-main"
        :class="addCotentClass"
        :style="{ top: appContentTop }"
      >
        <RouterView />
      </div>

      <BlogLinkContainer
        class="app__blogLinkContainer"
        :search-form-is-displayed="searchFormIsDisplayed"
        :filter-form-is-displayed="filterFormIsDisplayed"
      />

      <Transition name="fade">
        <div v-if="isDownloading" class="app__csvDownloadProgress">
          <CsvDownloadProgress :file-name="csvFileName" />
        </div>
      </Transition>
    </template>

    <AlertDialog
      data-cy="alert-dialog"
      :message="alertMessage"
      @close="closeAlertModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import AlertDialog from "@/components/dialog/AlertDialog.vue";
import Loading from "@/components/Loading.vue";
import IconTextButton from "@/components/IconTextButton.vue";
import { Icons } from "@/const/Icons";
import Header from "@/views/Header.vue";
import SettingNav from "@/views/SettingNav.vue";
import { FavoriteSearch } from "@/models/search/FavoriteSearch";
import SavedSearchBalloon from "@/components/search/SavedSearchBalloon.vue";
import BlogLinkContainer from "@/views/BlogLinkContainer.vue";
import ScrollWrapper, { Overflow } from "@/components/ScrollWrapper.vue";
import { stopWindowScroll, startWindowScroll } from "@/util/window-util";
import { Os } from "@/const/UserAgent";
import { getOs } from "@/util/user-agent-util";
import CsvDownloadProgress from "@/components/CsvDownloadProgress.vue";
import { getCsvFileName } from "@/util/csv-util";
import V3SideBarNew from "@/components/v3-side-bar/V3SideBarNew.vue";
import { sideBarStore } from "@/store/v3-side-bar";
import { useSearchBalloon } from "@/v3-composables/use-search-balloon";

const { toggleIsSideBarMinimized, isSideBarMinimized } = sideBarStore();
const { resetSearchBalloonStats } = useSearchBalloon();

const SHOW_BALLOON_WAIT_DURATION = 300;
const HIDE_BALLOON_WAIT_DURATION = 100;
const HEADER_HEIGHT = 70;

@Component({
  components: {
    Header,
    V3SideBarNew,
    SettingNav,
    AlertDialog,
    Loading,
    IconTextButton,
    BlogLinkContainer,
    SavedSearchBalloon,
    ScrollWrapper,
    CsvDownloadProgress
  }
})
export default class DefaultLayout extends Vue {
  hoverExpandGlobalNav = false;
  globalNavShowTimer: number | null = null;

  // whether to display balloon or not
  lastHoveredSearchId: number | null = null;
  balloonDisplayTimer: number | null = null;
  balloonHideTimer: number | null = null;
  isItemHover: boolean = false;

  // where to display balloon
  balloonPositionX: string = "260px";
  navScroll: number = 0;
  homeScroll: number = 0;
  overflow = Overflow;

  icons = Icons;

  appContentTop: string = "";

  searchFormIsDisplayed = false;
  filterFormIsDisplayed = false;

  @Watch("$route")
  pageTransition(to: Route, from: Route) {
    if (to.name !== from.name) {
      this.appContentTop = "";
      this.startWindowScrollWithScrollbarDisplay();
    }
  }

  clickBurger() {
    toggleIsSideBarMinimized();
  }

  updateIsBalloonHover(isBalloonHover: boolean) {
    this.$store.commit(
      "preference/setIsFavoriteSearchBalloonHovered",
      isBalloonHover
    );
  }

  onMouseEnterSavedSearchBalloon() {
    this.updateIsBalloonHover(true);
    // setTimeout 使わずに表示したいので onMouseEnterNavi を使わない
    this.hoverExpandGlobalNav = true;
    this.stopWindowScrollWithScrollbarDisplay();

    if (this.balloonDisplayTimer !== null) {
      clearTimeout(this.balloonDisplayTimer);
    }
  }

  onMouseLeaveSavedSearchBalloon() {
    this.updateIsBalloonHover(false);

    // hide balloon when move out of cell
    this.balloonDisplayTimer = window.setTimeout(() => {
      if (!this.isBalloonHover) {
        this.onMouseLeaveNavi();
        resetSearchBalloonStats();
      }
    }, HIDE_BALLOON_WAIT_DURATION);
  }

  mounted() {
    resetSearchBalloonStats();
  }

  onMouseEnterNavi() {
    this.globalNavShowTimer = window.setTimeout(() => {
      this.hoverExpandGlobalNav = true;
      this.stopWindowScrollWithScrollbarDisplay();
    }, SHOW_BALLOON_WAIT_DURATION);
  }

  onMouseLeaveNavi() {
    this.hoverExpandGlobalNav = false;
    this.startWindowScrollWithScrollbarDisplay();

    if (this.globalNavShowTimer !== null) {
      clearTimeout(this.globalNavShowTimer);
    }
  }

  changeSearchFormIsDisplayed(isDisplayed: boolean) {
    this.searchFormIsDisplayed = isDisplayed;
  }

  changeFilterFormIsDisplayed(isDisplayed: boolean) {
    this.filterFormIsDisplayed = isDisplayed;
  }

  get addCotentClass(): string[] {
    const result: string[] = [];

    if (!this.isLoggedInAndAuthenticated || this.shouldHideHeaderAndSideBar) {
      return result;
    }

    if (isSideBarMinimized.value) {
      result.push("app__content--condensed");
    } else {
      result.push("app__content--expanded");
    }

    if (this.hoverExpandGlobalNav && this.isOsWindow) {
      result.push("app__content--hoverExpanded");
    }

    return result;
  }

  get style() {
    return {
      left: this.balloonPositionX,
      top: this.balloonPositionY
    };
  }

  get isInitialized(): boolean {
    return (
      this.$store.state.app.isInitialized || this.$route.meta?.allowPublicAccess
    );
  }

  get isAuthenticated(): boolean {
    return this.$store.state.auth.isAuthenticated;
  }

  get isLoggedIn(): boolean {
    return this.$route.path !== "/login";
  }

  get isLoggedInAndAuthenticated(): boolean {
    return this.isLoggedIn && this.isAuthenticated;
  }

  get shouldHideHeaderAndSideBar(): boolean {
    return !!(this.$route.meta && this.$route.meta.shouldHideHeaderAndSideBar);
  }

  get alertMessage(): string {
    const message = this.$store.state.alert.message;
    return message === null ? "" : message;
  }

  get showBalloon(): boolean {
    return this.balloonPositionY !== "";
  }

  get isOsWindow(): boolean {
    return getOs() === Os.Win;
  }

  get balloonPositionY(): string {
    return this.$store.state.preference.favoriteSearchBalloonPositionY;
  }

  get balloonArrowAdjust(): number {
    return this.$store.state.preference.favoriteSearchBalloonArrowAdjust;
  }

  get hoveredFavoriteSearch(): FavoriteSearch {
    return this.$store.state.preference.hoveredFavoriteSearch;
  }
  get isBalloonHover(): boolean {
    return this.$store.state.preference.isFavoriteSearchBalloonHovered;
  }

  closeAlertModal() {
    this.$store.commit("alert/initialize");
  }

  stopWindowScrollWithScrollbarDisplay() {
    if (this.isOsWindow) {
      const scrollY = window.pageYOffset;
      this.appContentTop = `-${scrollY - HEADER_HEIGHT}px`;
    } else {
      stopWindowScroll();
    }
  }

  startWindowScrollWithScrollbarDisplay() {
    if (this.isOsWindow) {
      this.$nextTick(() => {
        // app_savedSearchBalloon をマウスオーバーした際に appContentTop は初期化されているため、
        // Javascriptを使用し直接取得
        const appContent = this.$refs.appContent as HTMLElement;
        const appContentTop = appContent.style.top;
        const scrollY = parseInt(appContentTop || "0");

        this.appContentTop = "";
        if (0 > scrollY) {
          window.scrollTo(0, -(scrollY - HEADER_HEIGHT));
        }
      });
    } else {
      startWindowScroll();
    }
  }

  get isDownloading(): boolean {
    return this.$store.state.search.isCsvDownloading;
  }

  get csvFileName(): string {
    return getCsvFileName();
  }
}
</script>

<style lang="scss">
.default-layout {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  width: 100%;
  max-width: 100vw;
}

.default-layout__top-bar {
  position: sticky;
  top: 0%;
  grid-column: 1 / 3;
}

.default-layout__side-bar {
  grid-column: 1;
  grid-row: 2;
}

.default-layout__main {
  grid-column: 2;
  grid-row: 2;
}

.default-layout__loading {
  grid-column: span 2;
}

.app_savedSearchBalloon {
  position: fixed;
}

@media print {
  .app__blogLinkContainer {
    display: none;
  }
}

.app__csvDownloadProgress {
  position: fixed;
  left: 30px;
  bottom: 30px;
  z-index: 23;
}
</style>
